import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Paper,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import api from "./PlanMock.js";

export default function Subscription() {
  const [subscription, setSubscription] = useState({});
  const [allPlans, setAllPlans] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      const response = await api.get("subscription");
      setSubscription(response.data[0]);

      // Fetch all plans
      const plansResponse = await api.get("subscription-plans");
      setAllPlans(plansResponse.data);
    };
    fetchSubscriptionDetails();
  }, []);

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", my: 4, fontFamily: "Inter, sans-serif", display: "flex", flexDirection: "column", gap: 3, px: 2 }}>
      
      {/* Subscription Card */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, borderRadius: 3, overflow: "hidden", boxShadow: 3 }}>
        {/* Left Box */}
          <Box
            sx={{
              flex: 1,
              background: "linear-gradient(135deg, #d7e7ff, #ede5ff)", // unchanged
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              textAlign: "left",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                letterSpacing: 0.6,
              }}
            >
              YOUR CURRENT PLAN
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: 22, md: 28 },
                  fontWeight: 700,
                }}
              >
                {subscription.plan}
              </Typography>

              {/* Status unchanged */}
              <Chip
                label="Active"
                sx={{
                  backgroundColor: "#0a8a3a",
                  color: "white",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              />
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: "#333",
                mt: -1,
                fontSize: { xs: 14, md: 16 },
                fontWeight: 600,
              }}
            >
              {subscription.price}
              <span style={{ fontWeight: 400 }}>
                {" "}
                / {subscription.billing_cycle}
              </span>
            </Typography>

            {/* Dates – label/value differentiation */}
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  Subscription start:
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {subscription.start_date}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  Renews on:
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {subscription.end_date}
                </Typography>
              </Box>
            </Box>
          </Box>

        {/* Right Box */}
          <Box
              sx={{
                flex: 1,
                background: "linear-gradient(135deg, #d0f0e0, #a0e0c0)", // unchanged
                p: { xs: 3, md: 4 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
                textAlign: "left",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  letterSpacing: 0.6,
                }}
              >
                PLAN FEATURES
              </Typography>

              <List dense sx={{ p: 0 }}>
                <ListItem sx={{ py: 0.75 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <DescriptionIcon sx={{ mr: 0.5 }} />
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: 14 }}>
                        <strong>{subscription.features?.collaterals || 0}</strong>{" "}
                        collaterals
                      </Typography>
                    }
                  />
                </ListItem>

                <ListItem sx={{ py: 0.75 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <AttachMoneyIcon sx={{ mr: 0.5 }} />
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: 14 }}>
                        <strong>{subscription.features?.overage || 0}¢</strong> per mailer
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
          </Box>
      </Box>

      {/* Upgrade Section */}
      <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, textAlign: "center", background: "rgba(17,17,17,0.8)", color: "white", backdropFilter: "blur(8px)", boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: 16, md: 18 } }}>Ready for More Features?</Typography>
      <Box
  sx={{
    display: "flex",
    gap: 2,
    mt: 2,
    justifyContent: "center",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: "center",
  }}
>
  <Button
    variant="contained"
    sx={{
      minWidth: 180,
      py: 1.5,
      fontSize: { xs: 14, md: 16 },
      borderRadius: 2,
      background: "linear-gradient(to right, #0066ff, #0047b3)",
    }}
    onClick={() => window.open(process.env.REACT_APP_TRAQR_SUPPORT_PAGE_URL, "_blank")}
  >
    CHAT WITH US
  </Button>

  <Button
    variant="outlined"
    sx={{
      minWidth: 180,
      py: 1.5,
      fontSize: { xs: 14, md: 16 },
      borderRadius: 2,
      borderColor: "#00bfff",
      color: "#00bfff",
    }}
    onClick={() => setOpenModal(true)}
  >
    EXPLORE PLANS
  </Button>
</Box>
      </Box>

      {/* Plans Modal with Close Icon and Discount Message */}
     <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="lg" fullWidth>
  <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>All Plans</Typography>
    <IconButton onClick={() => setOpenModal(false)}><CloseIcon /></IconButton>
  </DialogTitle>
  <DialogContent sx={{ mt: 1 }}>
    {/* Attractive discount badge */}
   <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
  <Typography sx={{
    background: "linear-gradient(90deg, #0066ff, #ff3b3b)", // blue to red
    color: "#fff",
    fontWeight: 700,
    px: 2,
    py: 0.5,
    borderRadius: 2,
    boxShadow: 1,
    fontSize: { xs: 14, md: 16 }
  }}>
     Get 2 months FREE with Annual Subscription!
  </Typography>
</Box>


    {/* Plans Grid */}
    <Grid container spacing={4} justifyContent="center">
      {allPlans.map((plan) => (
        <Grid item xs={12} sm={6} md={3} key={plan.name}>
          <Paper
  elevation={1}
  sx={{
    p: 3,
    borderRadius: 3,
    textAlign: "center",
    border: "1px solid #e5e7eb",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  }}
>
  {/* Plan Name */}
  <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 2 }}>
    {plan.name}
  </Typography>

  {/* Price Section */}
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontSize: 32, fontWeight: 700, color: "#1a1a1a" }}>
      ${plan.monthly_price}
      <span style={{ fontSize: 16, color: "#6b7280", fontWeight: 400 }}>
        /mo
      </span>
    </Typography>

    <Typography
      sx={{
        fontSize: 16,
        color: "#6b7280",
        fontWeight: 500,
        mt: 1
      }}
    >
      Billed annually:
      <strong style={{ marginLeft: 4, color: "#1a1a1a" }}>
        ${plan.annual_price}
      </strong>
    </Typography>
  </Box>

  <Divider sx={{ my: 2 }} />

  {/* Features */}
  <List dense sx={{ p: 0, textAlign: "left" }}>
    <ListItem sx={{ py: 0.5 }}>
      <CheckCircleIcon sx={{ color: "#0ea65a", mr: 1 }} />
      <ListItemText
        primary={`Collaterals: ${plan.quota?.collaterals || 0}`}
        primaryTypographyProps={{ fontSize: 14 }}
      />
    </ListItem>

    <ListItem sx={{ py: 0.5 }}>
      <CheckCircleIcon sx={{ color: "#0ea65a", mr: 1 }} />
      <ListItemText
        primary={`Overage: ${plan?.overage_price || 0}c/mailer`}
        primaryTypographyProps={{ fontSize: 14 }}
      />
    </ListItem>
  </List>
</Paper>


        </Grid>
      ))}
    </Grid>
  </DialogContent>
</Dialog>

    </Box>
  );
}
